import React, { PropsWithChildren, useMemo } from 'react'
import FixedTooltip from '~/app/components/common/Tooltip/FixedTooltip'
import { OrderCancelPolicyDomestic } from '~/app/components/order-cancel/hooks/useOrderCancelPolicy'
import { CancelPolicyCreteria } from '~/__generated__/__types__'
import { DateTime, Duration, Interval } from 'luxon'
import { styled } from '~/app/components'
import { Typography } from '@yanolja-org/ya-com-core'

/**
 * CancelPoliciesTooltip component
 * author: soojin.oh
 */

// type definitions
type Props = {
  cancelPolicies: OrderCancelPolicyDomestic['cancelPolicies']
  checkInAt: string
  checkOutAt: string
  currentAt: string
}

// styled components
const Title = styled.div`
  ${Typography('body2.multiline').regular}
  margin-bottom: 0.6rem;
`
const Description = styled.div`
  ${Typography('body2.multiline').regular}

  ::before {
    content: '•';
    display: inline-block;
    width: 1.5rem;
  }
`

// CancelPoliciesTooltip component
const CancelPoliciesTooltip = function ({
  children,
  cancelPolicies,
  checkInAt,
  checkOutAt,
  currentAt,
}: PropsWithChildren<Props>) {
  const content = useMemo(() => {
    const checkIn = DateTime.fromISO(checkInAt).toFormat('yyyy.MM.dd')
    const checkOut = DateTime.fromISO(checkOutAt).toFormat('yyyy.MM.dd')
    const interval = Interval.fromDateTimes(
      DateTime.fromFormat(checkIn, 'yyyy.MM.dd'),
      DateTime.fromFormat(checkOut, 'yyyy.MM.dd')
    )
    const now = DateTime.fromISO(currentAt)
    const policyPerDay = interval
      .splitBy({ day: 1 }) // 1일 간격의 interval 생성
      .map(({ start }) => {
        /**
         * FIXME: cancelPolicies
         * - 추후 개편시 /cancel-policies/{id}/check API 변경 필요 여부 확인 필요
         * - 현재는 nullable 처리되어 있어 운영상 이슈 없음.
         * {@link https://yanoljagroup.atlassian.net/wiki/spaces/platformdiv/pages/1318229415/Configuration+API+-#GET-%2Fcancel-policies%2F%7Bid%7D%2Fcheck-%5BinlineExtension%5D}
         */
        // 취소 정책들 중 알맞은 정책 찾기
        // 현재일 기준 적용 가능한 정책 찾기
        const validPolicy = cancelPolicies.policies.filter(policy => {
          const endTime = Duration.fromISOTime(policy.endTime)
          const endDate = start.minus({ day: policy.endDays }).plus({ hour: endTime.hours })
          const diff = now.diff(endDate).milliseconds
          return diff < 0
        })
        // 적용 가능한 정책 중 endDays 가 가장 큰 값이 현재일에 제일 적합한 정책
        const todayPolicy = validPolicy.reduce(
          (selected, current) => (selected.endDays > current.endDays ? selected : current),
          validPolicy[0]
        )
        const date = start.toFormat('yyyy.MM.dd')
        return {
          date,
          ratio: todayPolicy?.ratio || 100,
          criteria: todayPolicy?.criteria || CancelPolicyCreteria.Total,
        }
      })

    if (policyPerDay.length <= 0) {
      return <></>
    }
    return (
      <>
        <Title>연박 투숙일별 취소 수수료율 안내</Title>
        {policyPerDay.map(({ date, ratio, criteria }, index) => {
          if (criteria === CancelPolicyCreteria.Free) {
            return <Description key={index}>{date} 취소 수수료율 0%</Description>
          } else if (criteria === CancelPolicyCreteria.Total) {
            return <Description key={index}>{date} 취소 수수료율 100%</Description>
          } else if (criteria === CancelPolicyCreteria.Ratio) {
            return (
              <Description key={index}>
                {date} 취소 수수료율 {ratio}%
              </Description>
            )
          }
        })}
      </>
    )
  }, [cancelPolicies, checkInAt, checkOutAt, currentAt])
  return (
    <FixedTooltip
      stickyTo={'right'}
      content={content}
      includeCloseIcon={false}
      childrenStyle={{ display: 'flex', justifyContent: 'flex-end' }}
    >
      {children}
    </FixedTooltip>
  )
}

export default CancelPoliciesTooltip
